import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Camera, ChevronLeft, Loader2, Send, CheckCircle2, X } from "lucide-react";
import { Input } from "../../components/ui/input";
import { SevBadge } from "../../components/Bits";
import { api, errMsg, photoSrc } from "../../lib/api";
import { compress } from "../../lib/image";

const ST = [["OPEN", "Open", "bg-slate-700"], ["IN_PROGRESS", "In progress", "bg-amber-500"], ["FIXED", "Fixed", "bg-emerald-600"]];

function DefectRow({ d, iid, locked, onSaved }) {
  const r = d.rectification;
  const cam = useRef(null);
  const [note, setNote] = useState(r.note || "");
  const [busy, setBusy] = useState(false);
  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/builder/inspections/${iid}/rectify`, { defect_id: d.defect_id, status: r.status, note, photos: r.photos || [], ...patch });
      onSaved(d.defect_id, data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const addPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const { data } = await api.post("/photos", { data_url: await compress(f), inspection_id: iid });
      await save({ photos: [...(r.photos || []), data.photo_id].slice(-4) });
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`builder-defect-${d.number}`}>
      <div className="flex items-center justify-between"><span className="font-mono text-xs font-bold">#{d.number} · {d.room} · {d.category}</span><SevBadge sev={d.severity} /></div>
      <div className="mt-1 font-display text-lg font-bold">{d.name}</div>
      <div className="mt-3 grid gap-3 md:grid-cols-[1fr_1.4fr]">
        <div className="grid grid-cols-2 gap-2">
          <div><div className="mb-1 font-mono text-[10px] text-slate-400">INSPECTOR PHOTO</div>{d.photos?.[0] ? <img src={photoSrc(d.photos[0])} alt="defect" className="aspect-[4/3] w-full rounded-md object-cover" /> : <div className="aspect-[4/3] rounded-md bg-slate-100" />}</div>
          <div><div className="mb-1 font-mono text-[10px] text-slate-400">FIX PHOTO</div>
            {r.photos?.length ? (
              <div className="relative"><img src={photoSrc(r.photos[r.photos.length - 1])} alt="fix" className="aspect-[4/3] w-full rounded-md object-cover" />
                {!locked && <button onClick={() => save({ photos: r.photos.slice(0, -1) })} className="absolute right-1 top-1 rounded bg-slate-900/80 p-1 text-white" data-testid={`builder-remove-photo-${d.number}`}><X className="h-3 w-3" /></button>}
              </div>
            ) : <button disabled={locked} onClick={() => cam.current.click()} className="tap grid aspect-[4/3] w-full place-items-center rounded-md border-2 border-dashed border-slate-300 text-slate-400" data-testid={`builder-photo-${d.number}`}><Camera className="h-6 w-6" /></button>}
          </div>
        </div>
        <div className="text-sm">
          <p><b>Observation:</b> {d.remark}</p>
          <p className="mt-1 text-slate-600"><b>Recommendation:</b> {d.recommendation}</p>
          <div className="mt-3 grid grid-cols-3 gap-1.5">
            {ST.map(([k, l, c]) => <button key={k} disabled={locked || busy} onClick={() => save({ status: k })} className={`tap h-11 rounded-md border text-xs font-bold ${r.status === k ? `${c} border-transparent text-white` : "border-slate-200"}`} data-testid={`builder-status-${d.number}-${k.toLowerCase()}`}>{l.toUpperCase()}</button>)}
          </div>
          <div className="mt-2 flex gap-2">
            <Input disabled={locked} className="h-11" placeholder="What was done (e.g. tile replaced, re-grouted)" value={note} onChange={(e) => setNote(e.target.value)} onBlur={() => note !== (r.note || "") && save({})} data-testid={`builder-note-${d.number}`} />
            {!locked && <button onClick={() => cam.current.click()} className="tap grid h-11 w-11 shrink-0 place-items-center rounded-md bg-slate-900 text-teal-300" data-testid={`builder-add-photo-${d.number}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}</button>}
          </div>
        </div>
      </div>
      <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={addPhoto} />
    </div>
  );
}

export default function BuilderDetail() {
  const { iid } = useParams();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/builder/inspections/${iid}`).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))); }, [iid]);
  if (!d) return <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>;
  const fixed = d.defects.filter((x) => x.rectification.status === "FIXED").length;
  const locked = !!d.rect_meta?.reinspection_id;
  const onSaved = (id, r) => setD((x) => ({ ...x, defects: x.defects.map((y) => (y.defect_id === id ? { ...y, rectification: r } : y)) }));
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/builder/inspections/${iid}/submit`); setD((x) => ({ ...x, rect_meta: data })); toast.success(`Re-inspection ${data.reinspection_id} scheduled automatically`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mx-auto max-w-5xl pb-28" data-testid="builder-detail">
      <Link to="/builder" className="mb-3 flex items-center gap-1 text-sm font-semibold text-slate-600" data-testid="builder-back-link"><ChevronLeft className="h-4 w-4" />All reports</Link>
      <div className="overline mb-1">{d.inspection_id} · inspected {d.inspection_date}</div>
      <h1 className="font-display text-3xl font-extrabold">{d.property.project} · {d.property.flat}</h1>
      <p className="mt-1 text-sm text-slate-500">{d.property.location} · {d.property.bhk} BHK · customer details are kept private</p>
      {locked && <div className="mt-4 flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="builder-submitted-banner"><CheckCircle2 className="h-5 w-5" />Submitted {new Date(d.rect_meta.submitted_at).toLocaleString("en-IN")} · re-inspection <b className="font-mono">{d.rect_meta.reinspection_id}</b> scheduled.</div>}
      <div className="mt-6 space-y-3">{d.defects.map((x) => <DefectRow key={x.defect_id} d={x} iid={iid} locked={locked} onSaved={onSaved} />)}</div>
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/90 p-3 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center gap-3">
          <div className="flex-1 font-mono text-xs" data-testid="builder-fixed-count">{fixed}/{d.defects.length} FIXED</div>
          <button disabled={locked || busy || fixed < d.defects.length} onClick={submit} className="tap flex h-12 items-center gap-2 rounded-md bg-slate-900 px-6 font-bold text-white disabled:opacity-40" data-testid="builder-submit-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}{locked ? "SUBMITTED" : "SUBMIT FOR RE-INSPECTION"}
          </button>
        </div>
      </div>
    </div>
  );
}
